<template>
    <div class="body bank-app">
        <Navbar :show-back-button="true" @back="goBack"/>
        
        <div class="page-header">
            <h2 class="page-title">📋 {{ title }}</h2>
            <div class="header-actions">
                <button 
                    v-if="detailData.length > 0 && !loading"
                    class="print-btn"
                    @click="printReport"
                    title="چاپ گزارش"
                >
                    🖨️ چاپ
                </button>
                <button class="refresh-btn" @click="fetchData" :disabled="loading">
                    <span class="refresh-icon" :class="{ spinning: loading }">⟳</span>
                    {{ loading ? 'در حال بارگیری...' : 'بروزرسانی' }}
                </button>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
            <div class="spinner"></div>
            <p>در حال بارگیری اطلاعات...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
            <div class="error-icon">⚠️</div>
            <p>{{ error }}</p>
            <button @click="fetchData" class="retry-btn">تلاش مجدد</button>
        </div>

        <!-- Data Display -->
        <div v-else-if="detailData.length > 0" class="report-content">
            <!-- Summary Cards -->
            <div class="summary-cards">
                <div class="summary-card">
                    <div class="summary-icon">📊</div>
                    <div class="summary-info">
                        <span class="summary-label">تعداد کل</span>
                        <span class="summary-value">{{ totalCount }}</span>
                    </div>
                </div>
                <div class="summary-card">
                    <div class="summary-icon">💰</div>
                    <div class="summary-info">
                        <span class="summary-label">مجموع مبلغ</span>
                        <span class="summary-value">{{ formatCurrencyPrice(totalAmount) }}</span>
                    </div>
                </div>
            </div>

            <!-- Desktop Table View -->
            <div class="table-container desktop-view">
                <table class="report-table">
                    <thead>
                        <tr>
                            <th v-if="showInvoiceColumn">شماره فاکتور</th>
                            <th>مشتری</th>
                            <th v-if="showPriceColumn">قیمت</th>
                            <th v-if="showPriceDecColumn">کسورات</th>
                            <th v-if="showPriceIncColumn">اضافات</th>
                            <th v-if="showPriceAllColumn">قیمت کل</th>
                            <th>ارز</th>
                            <th>عملیات</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in detailData" :key="row.Invoice_Auto || row.Exhibit_Auto || row.cost">
                            <td v-if="showInvoiceColumn">{{ row.Invoice_Auto || '-' }}</td>
                            <td>{{ row.cost || '-' }}</td>
                            <td v-if="showPriceColumn">{{ formatCurrencyPrice(row.Price) }}</td>
                            <td v-if="showPriceDecColumn">{{ formatCurrencyPrice(row.PriceDec) }}</td>
                            <td v-if="showPriceIncColumn">{{ formatCurrencyPrice(row.PriceInc) }}</td>
                            <td v-if="showPriceAllColumn">{{ formatCurrencyPrice(row.PriceAll) }}</td>
                            <td>{{ row.Arz || '-' }}</td>
                            <td>
                                <button 
                                    class="view-details-btn"
                                    @click.stop="openCommercialDetails(row)"
                                >
                                    <span class="btn-icon">👁️</span>
                                    جزئیات
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Mobile Card View -->
            <div class="mobile-view">
                <div v-for="(row, index) in detailData" :key="index" class="detail-card">
                    <div class="card-header">
                        <span class="card-index">#{{ index + 1 }}</span>
                        <span class="card-customer">{{ row.cost || 'مشتری' }}</span>
                    </div>
                    <div class="card-body">
                        <div v-if="showInvoiceColumn" class="card-row">
                            <span class="card-label">شماره فاکتور</span>
                            <span class="card-value">{{ row.Invoice_Auto || '-' }}</span>
                        </div>
                        <div v-if="showPriceColumn && row.Price!=row.PriceAll" class="card-row">
                            <span class="card-label">قیمت</span>
                            <span class="card-value">{{ formatCurrencyPrice(row.Price) }}</span>
                        </div>
                        <div v-if="showPriceDecColumn" class="card-row">
                            <span class="card-label">کسورات</span>
                            <span class="card-value">{{ formatCurrencyPrice(row.PriceDec) }}</span>
                        </div>
                        <div v-if="showPriceIncColumn" class="card-row">
                            <span class="card-label">اضافات</span>
                            <span class="card-value">{{ formatCurrencyPrice(row.PriceInc) }}</span>
                        </div>
                        <div v-if="showPriceAllColumn" class="card-row">
                            <span class="card-label">قیمت کل</span>
                            <span class="card-value">{{ formatCurrencyPrice(row.PriceAll) }}</span>
                        </div>
                        <div class="card-row">
                            <span class="card-label">ارز</span>
                            <span class="card-value">{{ row.Arz || '-' }}</span>
                        </div>
                        <div class="kala-actions">
                            <button 
                                class="view-details-btn"
                                @click.stop="openCommercialDetails(row)"
                            >
                                <span class="btn-icon">👁️</span>
                                مشاهده جزئیات
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading && !error" class="empty-state">
            <div class="empty-icon">📭</div>
            <h3>داده‌ای وجود ندارد</h3>
            <p>هیچ اطلاعاتی برای نمایش یافت نشد</p>
        </div>

        <!-- Commercial Details Modal -->
        <CommercialDetailsModal
            :visible="showCommercialModal"
            :invoice-id="selectedInvoiceId"
            :ac="selectedAc"
            :act-code="selectedActCode"
            @update:visible="showCommercialModal = $event"
            @print="printCommercialDetails"
            @close="closeCommercialModal"
        />
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import Navbar from '@/components/Navbar.vue'
import { employerService } from '@/services/api'
import CommercialDetailsModal from '@/components/CommercialDetailsModal.vue'

export default {
    name: 'DailyReportDetail',
    components: {
        Navbar,
        CommercialDetailsModal
    },
    setup() {
        const router = useRouter()
        const route = useRoute()
        
        // State
        const detailData = ref([])
        const title = ref('')
        const loading = ref(false)
        const error = ref(null)
        const act = ref(null)
        const reportDate = ref('')
        
        // Commercial Modal State
        const showCommercialModal = ref(false)
        const selectedInvoiceId = ref(null)
        const selectedAc = ref(null)
        const selectedActCode = ref(null)
        
        // Computed
        const totalCount = computed(() => detailData.value.length)
        
        const totalAmount = computed(() => {
            return detailData.value.reduce((sum, row) => sum + (row.PriceAll || row.Price || 0), 0)
        })
        
        const showInvoiceColumn = computed(() => {
            return detailData.value.some(row => row.Invoice_Auto && row.Invoice_Auto !== 0)
        })
        
        const showExhibitColumn = computed(() => {
            return detailData.value.some(row => row.Exhibit_Auto && row.Exhibit_Auto !== 0)
        })
        
        const showPriceColumn = computed(() => {
            return detailData.value.some(row => row.Price && row.Price !== 0)
        })
        
        const showPriceDecColumn = computed(() => {
            return detailData.value.some(row => row.PriceDec && row.PriceDec !== 0)
        })
        
        const showPriceIncColumn = computed(() => {
            return detailData.value.some(row => row.PriceInc && row.PriceInc !== 0)
        })
        
        const showPriceAllColumn = computed(() => {
            return detailData.value.some(row => row.PriceAll && row.PriceAll !== 0)
        })
        
        // Methods
        const goBack = () => {
            router.push('/employer/dashboard')
        }
        
        const fetchData = async () => {
            if (!act.value || !reportDate.value) {
                error.value = 'اطلاعات کافی برای بارگیری وجود ندارد'
                return
            }
            
            loading.value = true
            error.value = null
            
            try {
                const response = await employerService.getDailyDetailReport(act.value, reportDate.value)
                
                if (response.data.success) {
                    detailData.value = response.data.data || []
                } else {
                    error.value = response.data.message || 'خطا در دریافت اطلاعات'
                }
            } catch (err) {
                console.error('Error fetching detail report:', err)
                error.value = err.response?.data?.detail || err.message || 'خطا در اتصال به سرور'
            } finally {
                loading.value = false
            }
        }
        
        const toNumber = (value) => {
            if (value === null || value === undefined) return 0
            let str = String(value).trim()
            const persianDigits = '۰۱۲۳۴۵۶۷۸۹'
            const arabicDigits = '٠١٢٣٤٥٦٧٨٩'
            str = str.replace(/[۰-۹]/g, d => persianDigits.indexOf(d))
                    .replace(/[٠-٩]/g, d => arabicDigits.indexOf(d))
            str = str.replace(/[,\u060C\u066C]/g, '')
            const num = Number(str)
            return Number.isFinite(num) ? num : 0
        }
        
        const formatCurrencyPrice = (price) => {
            if (!price || price === "0" || price === 0) return '۰'
            try {
                const cleanPrice = price.toString().replace(/[,\u066B\u066C\u060C]/g, '')
                const numericStr = cleanPrice.replace(/[^\d.-]/g, '')
                const numPrice = parseFloat(numericStr)
                if (isNaN(numPrice)) return '۰'
                return numPrice.toLocaleString('fa-IR', {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0
                })
            } catch (error) {
                return price.toString()
            }
        }

        const formatNumber = (number) => {
            if (!number && number !== 0) return '۰'
            return new Intl.NumberFormat('fa-IR').format(number)
        }

        const formatCurrency = (amount) => {
            if (!amount && amount !== 0) return '۰'
            return new Intl.NumberFormat('fa-IR').format(amount)
        }

        const openCommercialDetails = (row) => {
            // Get the invoice ID from the row
            const invoiceId = row.Invoice_Auto || row.InvoiceAuto
            if (!invoiceId) {
                console.warn('No invoice ID found for this item')
                return
            }
            
            // Set the data for the modal
            selectedInvoiceId.value = invoiceId
            selectedAc.value = row.Ac || 2
            selectedActCode.value = row.Act || 279
            
            showCommercialModal.value = true
        }

        const closeCommercialModal = () => {
            showCommercialModal.value = false
            selectedInvoiceId.value = null
            selectedAc.value = null
            selectedActCode.value = null
        }

        const printCommercialDetails = () => {
            console.log('Printing commercial details...')
            // The print logic is handled inside the CommercialDetailsModal component
        }

        const printReport = () => {
            if (!detailData.value || detailData.value.length === 0) {
                alert('هیچ داده‌ای برای چاپ وجود ندارد')
                return
            }
            
            const printWindow = window.open('', '_blank', 'width=1200,height=800')
            
            if (!printWindow) {
                alert('لطفاً مسدود کننده پنجره‌های بازشو را غیرفعال کنید')
                return
            }
            
            // Generate table rows
            const tableRows = detailData.value.map(row => {
                let columns = ''
                if (showInvoiceColumn.value) columns += `<td>${row.Invoice_Auto || '-'}</td>`
                if (showExhibitColumn.value) columns += `<td>${row.Exhibit_Auto || '-'}</td>`
                columns += `<td>${row.cost || '-'}</td>`
                if (showPriceColumn.value) columns += `<td>${formatCurrencyPrice(row.Price)}</td>`
                if (showPriceDecColumn.value) columns += `<td>${formatCurrencyPrice(row.PriceDec)}</td>`
                if (showPriceIncColumn.value) columns += `<td>${formatCurrencyPrice(row.PriceInc)}</td>`
                if (showPriceAllColumn.value) columns += `<td>${formatCurrencyPrice(row.PriceAll)}</td>`
                columns += `<td>${row.Arz || '-'}</td>`
                return `<tr>${columns}</tr>`
            }).join('')
            
            // Build table headers
            let headers = ''
            if (showInvoiceColumn.value) headers += '<th>شماره فاکتور</th>'
            if (showExhibitColumn.value) headers += '<th>شماره نمایشگاه</th>'
            headers += '<th>مشتری</th>'
            if (showPriceColumn.value) headers += '<th>قیمت</th>'
            if (showPriceDecColumn.value) headers += '<th>کسورات</th>'
            if (showPriceIncColumn.value) headers += '<th>اضافات</th>'
            if (showPriceAllColumn.value) headers += '<th>قیمت کل</th>'
            headers += '<th>ارز</th>'
            
            const getTotalColspan = () => {
                let count = 1
                if (showInvoiceColumn.value) count++
                if (showExhibitColumn.value) count++
                if (showPriceColumn.value) count++
                if (showPriceDecColumn.value) count++
                if (showPriceIncColumn.value) count++
                if (showPriceAllColumn.value) count++
                return count
            }
            
            const printContent = `
                <html dir="rtl">
                    <head>
                        <title>چاپ گزارش - ${title.value}</title>
                        <style>
                            * { margin: 0; padding: 0; box-sizing: border-box; }
                            body { 
                                font-family: 'Tahoma', 'Vazir', Arial, sans-serif; 
                                margin: 20px; padding: 20px;
                                direction: rtl;
                                line-height: 1.6;
                                background: #ffffff;
                                color: #333;
                            }
                            .print-header { 
                                text-align: center; 
                                margin-bottom: 30px;
                                padding-bottom: 15px;
                                border-bottom: 3px solid #667eea;
                            }
                            .print-header h1 {
                                margin: 0 0 10px 0;
                                color: #2d3436;
                                font-size: 24px;
                            }
                            .print-info {
                                display: flex;
                                justify-content: space-between;
                                flex-wrap: wrap;
                                margin-bottom: 20px;
                                padding: 15px 20px;
                                background: #f8f9fa;
                                border-radius: 8px;
                                border: 1px solid #e0e0e0;
                                gap: 10px;
                            }
                            .info-item {
                                display: flex;
                                flex-direction: column;
                            }
                            .info-label {
                                font-weight: bold;
                                color: #555;
                                margin-bottom: 5px;
                                font-size: 13px;
                            }
                            .info-value {
                                color: #333;
                                font-size: 14px;
                            }
                            .report-table {
                                width: 100%;
                                border-collapse: collapse;
                                margin-top: 20px;
                                font-size: 13px;
                            }
                            .report-table th {
                                background: #667eea;
                                color: white;
                                padding: 10px 8px;
                                text-align: center;
                                border: 1px solid #5a6fd6;
                                font-weight: bold;
                                font-size: 12px;
                            }
                            .report-table td {
                                padding: 8px 8px;
                                border: 1px solid #ddd;
                                text-align: center;
                                font-size: 12px;
                            }
                            .report-table tr:nth-child(even) {
                                background-color: #f8f9fa;
                            }
                            .total-row {
                                background-color: #e3f2fd !important;
                                font-weight: bold;
                            }
                            .footer {
                                margin-top: 30px;
                                text-align: center;
                                color: #888;
                                font-size: 12px;
                                border-top: 1px solid #eee;
                                padding-top: 20px;
                            }
                            @media print {
                                body { margin: 0; padding: 15px; font-size: 11px; }
                                .print-header { margin-bottom: 15px; padding-bottom: 10px; }
                                .print-header h1 { font-size: 20px; }
                                .print-info { padding: 10px 15px; margin-bottom: 15px; }
                                .report-table { font-size: 10px; }
                                .report-table th { padding: 6px 4px; font-size: 10px; }
                                .report-table td { padding: 5px 4px; font-size: 10px; }
                            }
                            @page { margin: 0.5cm; size: A4 portrait; }
                        </style>
                    </head>
                    <body>
                        <div class="print-header">
                            <h1>📄 گزارش ${title.value}</h1>
                            <div class="subtitle">تاریخ گزارش: ${reportDate.value || ''}</div>
                        </div>
                        
                        <div class="print-info">
                            <div class="info-item">
                                <span class="info-label">📅 تاریخ چاپ:</span>
                                <span class="info-value">${new Date().toLocaleDateString('fa-IR')}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">📊 تعداد آیتم‌ها:</span>
                                <span class="info-value">${totalCount.value} مورد</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">💰 مجموع مبالغ:</span>
                                <span class="info-value">${formatCurrencyPrice(totalAmount.value)}</span>
                            </div>
                        </div>
                        
                        <table class="report-table">
                            <thead><tr>${headers}</tr></thead>
                            <tbody>
                                ${tableRows}
                                <tr class="total-row">
                                    <td colspan="${getTotalColspan()}" style="text-align: left;">
                                        <strong>جمع کل:</strong>
                                    </td>
                                    <td><strong>${formatCurrencyPrice(totalAmount.value)}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                        
                        <div class="footer">
                            <p>الماس هوشمند - سیستم حسابداری پیشرفته</p>
                            <p>تاریخ تولید گزارش: ${new Date().toLocaleString('fa-IR')}</p>
                        </div>
                    </body>
                </html>
            `
            
            printWindow.document.write(printContent)
            printWindow.document.close()
            printWindow.focus()
            
            setTimeout(() => {
                printWindow.print()
            }, 1000)
        }
        
        // Lifecycle
        onMounted(() => {
            const params = route.params
            act.value = params.act || route.query.act
            title.value = params.title || route.query.title || 'جزئیات گزارش'
            reportDate.value = params.date || route.query.date || ''
            
            if (act.value && reportDate.value) {
                fetchData()
            } else {
                error.value = 'اطلاعات کافی برای نمایش وجود ندارد'
            }
        })
        
        return {
            detailData,
            title,
            loading,
            error,
            totalCount,
            totalAmount,
            showInvoiceColumn,
            showExhibitColumn,
            showPriceColumn,
            showPriceDecColumn,
            showPriceIncColumn,
            showPriceAllColumn,
            showCommercialModal,
            selectedInvoiceId,
            selectedAc,
            selectedActCode,
            goBack,
            fetchData,
            toNumber,
            formatCurrencyPrice,
            formatNumber,
            formatCurrency,
            openCommercialDetails,
            closeCommercialModal,
            printCommercialDetails,
            printReport
        }
    }
}
</script>

<style scoped>
.bank-app {
    min-height: 100vh;
    background: var(--bg);
    padding: 20px;
    direction: rtl;
    margin-bottom: 45px;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--card-bg);
    border-radius: 15px;
    padding: 20px 24px;
    margin-bottom: 20px;
    margin-top: 20px;
    box-shadow: var(--shadow-lg);
    border: 1px solid var(--border);
}

.page-title {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: var(--heading);
}

.header-actions {
    display: flex;
    gap: 10px;
}

.refresh-btn {
    padding: 8px 16px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--text);
}

.refresh-btn:hover:not(:disabled) {
    background: var(--ring);
    border-color: var(--primary);
}

.refresh-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.refresh-icon.spinning {
    animation: spin 1s linear infinite;
}

/* Summary Cards */
.summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.summary-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: var(--card-bg);
    border-radius: 12px;
    border: 1px solid var(--border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.summary-icon {
    font-size: 32px;
}

.summary-info {
    display: flex;
    flex-direction: column;
}

.summary-label {
    font-size: 13px;
    color: var(--text-muted);
}

.summary-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--heading);
}

/* Table Styles */
.table-container {
    overflow-x: auto;
    margin-bottom: 20px;
    border-radius: 12px;
    border: 1px solid var(--border);
    background: var(--card-bg);
}

.report-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    min-width: 700px;
}

.report-table thead {
    background: var(--bg);
    border-bottom: 2px solid var(--border);
}

.report-table th {
    padding: 12px 16px;
    text-align: right;
    font-weight: 600;
    color: var(--text-muted);
    white-space: nowrap;
    font-size: 13px;
}

.report-table td {
    padding: 10px 16px;
    border-bottom: 1px solid var(--border);
    color: var(--text);
}

.report-table tbody tr:hover {
    background: var(--hover);
}

/* Mobile Card View */
.mobile-view {
    display: none;
    flex-direction: column;
    gap: 12px;
}

.detail-card {
    background: var(--card-bg);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid var(--border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border);
    margin-bottom: 10px;
}

.card-index {
    font-weight: 700;
    color: var(--primary);
    font-size: 14px;
}

.card-customer {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
    flex: 1;
}

.card-body {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.card-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
}

.card-label {
    font-size: 12px;
    color: var(--text-muted);
    font-weight: 500;
}

.card-value {
    font-size: 13px;
    color: var(--text);
    font-weight: 600;
}

/* Responsive */
@media (max-width: 768px) {
    .desktop-view {
        display: none;
    }
    
    .mobile-view {
        display: flex;
    }
    
    .summary-cards {
        grid-template-columns: 1fr 1fr;
    }
    
    .page-header {
        flex-direction: column;
        gap: 12px;
        text-align: center;
    }
}

@media (max-width: 480px) {
    .summary-cards {
        grid-template-columns: 1fr;
    }
    
    .detail-card {
        padding: 12px;
    }
    
    .card-row {
        flex-wrap: wrap;
        gap: 4px;
    }
}

/* Loading State */
.loading-state {
    text-align: center;
    padding: 60px 20px;
}

.spinner {
    border: 4px solid var(--border);
    border-top: 4px solid var(--primary);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
}

/* Error State */
.error-state {
    text-align: center;
    padding: 40px 20px;
    background: #fff3cd;
    border-radius: 12px;
    border: 1px solid #ffc107;
}

.error-icon {
    font-size: 48px;
    margin-bottom: 12px;
}

.error-message {
    color: #856404;
    margin-bottom: 16px;
}

.retry-btn {
    padding: 10px 24px;
    background: var(--primary);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.3s ease;
}

.retry-btn:hover {
    background: var(--primary-hover);
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 60px 20px;
    background: var(--card-bg);
    border-radius: 12px;
    border: 1px dashed var(--border);
}

.empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
}

.empty-state h3 {
    margin: 0 0 8px;
    color: var(--heading);
}

.empty-state p {
    color: var(--text-muted);
}
.kala-actions {
    display: flex;
    gap: 10px;
}
.view-details-btn,
.add-to-cart-btn {
    padding: 8px 15px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    justify-content: center;
}

.view-details-btn {
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--border);
}

.view-details-btn:hover {
    background: var(--hover);
    transform: translateY(-2px);
}
.btn-icon {
    font-size: 18px;
}

.account-summary {
  background: linear-gradient(135deg, #4a6baf, #2c4a8c);
  color: white;
  border-radius: 16px;
  display: inline-flex;
  justify-content: center;
  padding: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  margin-left: 5px;
  opacity: 0.9;
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-label {
  font-size: 14px;
  opacity: 0.8;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
  margin-left: auto;
  margin-right: auto;
}
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #667eea;
}

.section-title {
  font-size: 1.1rem;
  color: #333;
  margin: 0;
}

.back-arrow {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.back-arrow:hover {
  background-color: #f0f0f0;
}
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 10px;
}

.print-btn {
  background: #2c3e50;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}

.print-btn:hover {
  background: #34495e;
}

.search-container {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  direction: rtl;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

.accounting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9000;
  padding: 20px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: white;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

/* Loading State */
.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.modal-loading .loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.modal-error {
  text-align: center;
  padding: 40px;
  color: #d63031;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.modal-error h4 {
  margin: 0 0 10px 0;
  color: #d63031;
}

.modal-error p {
  margin: 0 0 20px 0;
  color: #666;
}

.retry-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: transform 0.2s;
}

.retry-btn:hover {
  transform: translateY(-2px);
}

/* Empty State */
.modal-empty {
  text-align: center;
  padding: 40px;
  color: #666;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.modal-empty h4 {
  margin: 0 0 10px 0;
  color: #333;
}

/* Commercial Details Styles */
.commercial-details {
  margin-top: 10px;
}

.details-grid {
  display: grid;
  gap: 20px;
}

.detail-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.detail-section {
  margin-bottom: 20px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.info-box {
  background: white;
  border-radius: 6px;
  padding: 15px;
  border: 1px solid #e0e0e0;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
}

.info-value {
  color: #333;
  font-size: 0.9rem;
}

        /* Multi-values styling */
        .multi-values {
            display: flex;
            flex-direction: column;
            gap: 5px;
            flex: 1;
        }
        
        .multi-value-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 5px 0;
        }
        
        .multi-value-code {
            font-weight: 600;
            color: #4b6cb7;
            margin-left: 10px;
        }
        
        .multi-value-amount {
            color: #2c3e50;
            font-weight: 500;
        }

.info-value.amount {
  font-weight: 600;
  color: #2d3436;
}

.info-value.amount.total {
  color: #e74c3c;
  font-size: 1rem;
}

/* Details Button */
.details-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 10px;
  transition: all 0.3s ease;
  width: 100%;
}

.details-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

.details-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.close-modal-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.close-modal-btn:hover {
  background: #5a6268;
}

/* Responsive Design */
@media (max-width: 768px) {
  
  .modal-header {
    padding: 15px;
  }
  
  .detail-card {
    padding: 15px;
  }
  
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .info-label, .info-value {
    font-size: 0.85rem;
  }
}
.modal-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.print-modal-btn {
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.print-modal-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(39, 174, 96, 0.3);
}

/* Update modal header to accommodate the print button */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

/* Ensure the header content is properly aligned */
.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
  flex: 1;
}

/* Responsive adjustments for mobile */
@media (max-width: 768px) {
  .modal-actions {
    gap: 8px;
  }
  
  .print-modal-btn {
    padding: 6px 12px;
    font-size: 0.8rem;
  }
  
  .modal-header {
    padding: 15px;
  }
}
.accounting-section {
  padding-bottom: 45px;
  padding-top: 15px;
  background-color: var(--bg);
}

.accounting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.accounting-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.accounting-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  /*padding-bottom: 12px;
   border-bottom: 1px solid #f0f0f0; */
}

.record-date {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.record-amount {
  font-size: 14px;
  font-weight: bold;
}

.record-amount.debit {
  color: #e74c3c; /* Red for debit */
}

.record-amount.credit {
  color: #27ae60; /* Green for credit */
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.detail-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
  min-width: 60px;
}

.detail-value {
  font-size: 12px;
  color: #333;
  text-align: left;
  flex: 1;
}

.type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  text-align: center;
}

.debit-badge {
  background-color: #ffeaea;
  color: #e74c3c;
}

.credit-badge {
  background-color: #e8f7ef;
  color: #27ae60;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4a6baf;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #888;
  background: white;
  border-radius: 12px;
  border: 1px dashed #ddd;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .accounting-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .accounting-card {
    padding: 12px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .record-amount {
    align-self: flex-end;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .accounting-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1025px) {
  .accounting-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}



.debit-badge {
  background-color: #e74c3c; /* Red for بد */
}

.credit-badge {
  background-color: #27ae60; /* Green for بس */
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.debit {
  color: #e74c3c;
}

.credit {
  color: #27ae60;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
}

.comment-section {
  flex: 1;
  min-width: 0; /* Important for text truncation */
  text-align: right; /* For RTL languages */
}

.amount-section {
  display: flex;
  align-items: center;
  gap: 1px;
  flex-shrink: 0;
}

.detail-value {
  font-size: 12px;
  color: var(--text);
  text-align: right; /* For RTL */
  word-wrap: break-word;
  display: block;
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.transaction-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  font-size: 0.8rem;
  font-weight: bold;
  color: white;
}
.sort-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
}

.sort-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.sort-text {
  font-size: 12px;
  color: #495057;
}

.action-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
}
.action-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

/* Filter Button */
.filter-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
  position: relative;
}

.filter-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.filter-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Filter Panel */
.filter-panel {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.filter-header h4 {
  margin: 0;
  color: #495057;
}

.close-filter-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6c757d;
}

.filter-content {
  padding: 20px;
}

.filter-group {
  margin-bottom: 20px;
}

.filter-label {
  display: block;
  margin-bottom: 10px;
  font-weight: 600;
  color: #495057;
}

.filter-options {
  display: flex;
  gap: 15px;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.filter-option input[type="checkbox"] {
  margin: 0;
}

.date-inputs, .amount-inputs {
  display: flex;
  gap: 15px;
}

.date-input, .amount-input {
  flex: 1;
}

.date-input label, .amount-input label {
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  color: #6c757d;
}

.date-picker, .amount-picker {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
}

.filter-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.apply-filters-btn, .clear-filters-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.apply-filters-btn {
  background: #007bff;
  color: white;
}

.clear-filters-btn {
  background: #6c757d;
  color: white;
}

/* Active Filters */
.active-filters {
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
}

.active-filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.clear-all-filters {
  background: none;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  font-size: 12px;
}

.active-filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-tag {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-tag button {
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .search-container{
    width: 100%;
  }
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
  
  .date-inputs, .amount-inputs {
    flex-direction: column;
    gap: 10px;
  }
  
  .filter-options {
    flex-direction: column;
    gap: 10px;
  }
  
  .active-filter-tags {
    flex-direction: column;
  }
}
.currency-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 15px;
}

.currency-label {
  font-size: 0.9rem;
  color: #555;
  white-space: nowrap;
}

.currency-select {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  font-size: 0.9rem;
  min-width: 120px;
}

.currency-select:disabled {
  background-color: #f5f5f5;
  opacity: 0.7;
}

.currency-loading {
  font-size: 0.8rem;
  color: #666;
  white-space: nowrap;
}

/* Responsive design for currency selector */
@media (max-width: 768px) {
  .currency-selector {
    margin-right: 10px;
  }
  
  .currency-select {
    min-width: 100px;
    font-size: 0.8rem;
  }
  
  .currency-label {
    font-size: 0.8rem;
  }
}

/* Update the action-bar to accommodate the new currency selector */
.action-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 15px;
}

/* Ensure proper spacing in mobile view */
@media (max-width: 768px) {
  .action-bar {
    gap: 8px;
    padding: 10px;
  }
}

.info-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
}

.info-label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
  width: 120px; /* Fixed width for perfect alignment */
  text-align: right;
  flex-shrink: 0;
  margin-left: 10px;
}

.info-value {
  color: #333;
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  padding-right: 15px;
}

/* Mobile styles */
@media (max-width: 768px) {
    .info-row {
        flex-direction: row;
        align-items: center;
        padding: 10px 0;
    }
    
    .info-label {
        width: 100px;
        text-align: right;
        margin-left: 5px;
        margin-bottom: 0;
        font-size: 0.85rem;
    }
    
    .info-value {
        width: auto;
        text-align: right;
        padding-right: 5px;
        font-size: 0.85rem;
        font-weight: 500;
        flex: 1;
    }
}


/* Account Summary */

.account-summary {
  background: var(--primary-gradient);
  color: var(--text);
  border-radius: 16px;
  display: inline-flex;
  justify-content: center;
  padding: 10px;
  box-shadow: var(--shadow);
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  margin-left: 5px;
  opacity: .9;
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
  margin-left: auto;
  margin-right: auto;
}

/* Section */

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--primary);
}

.section-title {
  font-size: 1.1rem;
  color: var(--text);
  margin: 0;
}

.back-arrow {
  background: transparent;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  color: var(--text);
}

/* Action Bar */

.action-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 15px;
  background: var(--card-bg);
  border-radius: 10px;
  margin-bottom: 15px;
  border: 1px solid var(--border);
}

/* Buttons */

.print-btn {
  background: var(--primary);
  color: var(--on-primary);
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
}

.print-btn:hover {
  opacity: .9;
}

.sort-btn,
.filter-btn {
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text);
}

.sort-btn:hover,
.filter-btn:hover {
  background: var(--hover-bg);
}

.sort-text {
  font-size: 12px;
  color: var(--text-muted);
}

/* Search */

.search-container {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  background: var(--input-bg);
  color: var(--text);
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
}

/* Currency */

.currency-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.currency-label {
  font-size: .9rem;
  color: var(--text-muted);
}

.currency-select {
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--input-bg);
  color: var(--text);
}

/* Cards */

.accounting-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
  transition: .2s;
}

.accounting-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow);
}

.record-date {
  font-size: 12px;
  color: var(--text-muted);
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.debit {
  color: var(--danger);
}

.credit {
  color: var(--success);
}

/* Badges */

.transaction-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  font-size: .8rem;
  font-weight: bold;
  color: #fff;
}

.debit-badge {
  background: var(--danger);
}

.credit-badge {
  background: var(--success);
}

/* Modal */

.modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--overlay-bg);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9000;
  padding: 20px;
}

.modal-content {
  background: var(--card-bg);
  border-radius: 12px;

  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: var(--primary-gradient);
  color: var(--on-primary);
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 24px;
  color: var(--on-primary);
  cursor: pointer;
}

.modal-body {
  
  overflow-y: auto;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: flex-end;
}

.close-modal-btn {
  background: var(--secondary);
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
}

/* Loading */

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  color: var(--text-muted);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Empty */

.empty-state {
  text-align: center;
  padding: 40px;
  color: var(--text-muted);
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px dashed var(--border);
}

/* Filters */

.filter-panel {
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--shadow);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom: 1px solid var(--border);
}

.filter-label {
  font-weight: 600;
  color: var(--text);
}

.date-picker,
.amount-picker {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--input-bg);
  color: var(--text);
}

.apply-filters-btn {
  background: var(--primary);
  color: var(--on-primary);
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
}

.clear-filters-btn {
  background: var(--secondary);
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
}

/* Animations */

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Responsive */

@media (max-width:768px) {

  .search-container{
    width:100%;
  }

  .accounting-grid{
    grid-template-columns:1fr;
  }

}
</style>